import { AnyAction } from 'redux';
import { Category, DefaultMapping } from '@synergy/models';
import { unique } from '@synergy/utils';

import {
    CANCEL_CREATE_CATEGORY,
    CANCEL_DELETE_CATEGORY,
    CANCEL_PATCH_CATEGORY,
    CATEGORY_VISIBILITY_CHANGED,
    CREATE_CATEGORY_COMPLETED,
    DELETE_CATEGORY_COMPLETED,
    PATCH_CATEGORY_COMPLETED,
    RECEIVE_ALL_CATEGORIES,
    RECEIVE_CATEGORIES,
    RECEIVE_CATEGORY_DEFAULT_MAPPING,
    RECEIVE_CATEGORY_DEFAULT_MAPPINGS,
    REQUEST_CATEGORIES,
    START_CREATE_CATEGORY,
    START_DELETE_CATEGORY,
    START_PATCH_CATEGORY,
} from './../actions/category-actions';

export interface CategoryReducerState {
    items: Category[]; // Old fetch all mechanic
    defaultMappings: DefaultMapping<Category>[];
    requesting: boolean;
    categoriesByParentId: Record<number, Category[]>;
}

export const initialCategoryState: CategoryReducerState = {
    items: [],
    defaultMappings: [],
    requesting: false,
    categoriesByParentId: {},
};

export const categoryReducer = (
    state: CategoryReducerState = initialCategoryState,
    action: AnyAction
): CategoryReducerState => {
    const { type, ...rest } = action;

    switch (type) {
        case RECEIVE_CATEGORIES: {
            return {
                ...state,
                items: rest['categories'],
                requesting: false,
            };
        }

        case REQUEST_CATEGORIES:
        case START_DELETE_CATEGORY:
        case START_PATCH_CATEGORY:
        case START_CREATE_CATEGORY: {
            return {
                ...state,
                requesting: true,
            };
        }

        case CANCEL_CREATE_CATEGORY:
        case CANCEL_DELETE_CATEGORY:
        case CANCEL_PATCH_CATEGORY: {
            return {
                ...state,
                requesting: false,
            };
        }

        case PATCH_CATEGORY_COMPLETED: {
            const newCategories = [...state.items];
            const newCategory: Category = rest['category'];

            // Replace updated category
            const categoryIndex = newCategories.findIndex(category => category.id === newCategory.id);
            if (categoryIndex === -1) {
                return state;
            }
            newCategories[categoryIndex] = { ...newCategories[categoryIndex], ...newCategory };

            return {
                ...state,
                items: newCategories,
                requesting: false,
            };
        }

        case CREATE_CATEGORY_COMPLETED: {
            const newCategories = [...state.items, rest['category']];

            return {
                ...state,
                items: newCategories,
                requesting: false,
            };
        }

        case DELETE_CATEGORY_COMPLETED: {
            const newCategories = [...state.items];
            let toDelete: number[] = [rest['categoryId']];
            let foundItems: number[] = [rest['categoryId']];
            do {
                foundItems = state.items
                    // eslint-disable-next-line no-loop-func
                    .filter(item => item.parentId && foundItems.includes(item.parentId))
                    .map(item => item.id);
                toDelete = unique(toDelete.concat(foundItems));
            } while (foundItems.length > 0);

            toDelete.forEach(categoryId => {
                const categoryIndex = newCategories.findIndex(category => category.id === categoryId);
                if (categoryIndex >= 0) {
                    newCategories.splice(categoryIndex, 1);
                }
            });

            return {
                ...state,
                items: newCategories,
                requesting: false,
            };
        }

        case RECEIVE_CATEGORY_DEFAULT_MAPPING: {
            const defaultMappings = [...state.defaultMappings];
            const index = defaultMappings.findIndex(mapping => mapping.id === rest['defaultMapping'].id);
            if (index >= 0) {
                defaultMappings[index] = rest['defaultMapping'];
            }

            return {
                ...state,
                defaultMappings,
                requesting: false,
            };
        }

        case RECEIVE_CATEGORY_DEFAULT_MAPPINGS: {
            return {
                ...state,
                defaultMappings: rest['defaultMappings'],
                requesting: false,
            };
        }

        case CATEGORY_VISIBILITY_CHANGED: {
            const newCategories = [...state.items];
            const categoryIndex = newCategories.findIndex(category => category.id === rest['categoryId']);
            if (categoryIndex === -1) {
                return state;
            }
            newCategories[categoryIndex] = {
                ...newCategories[categoryIndex],
                isVisibleInShop: rest['isVisibleInShop'],
            };
            return {
                ...state,
                items: newCategories,
                requesting: false,
            };
        }

        case RECEIVE_ALL_CATEGORIES: {
            const parentId = (action['parentId'] as number) ?? -1;
            const categories = (action['categories'] as Category[]).filter(category => category.id !== parentId);

            return {
                ...state,
                categoriesByParentId: {
                    ...state.categoriesByParentId,
                    [parentId]: categories,
                },
            };
        }

        default: {
            return state;
        }
    }
};
